import { CircularProgress, Tooltip, useMediaQuery } from '@mui/material';
import { EmailRegexp, MIN_PASSWORD_LENGTH } from '../../shared/constants';
import React, {
  ChangeEvent,
  FormEvent,
  MouseEvent,
  useEffect,
  useState
} from 'react';
import { RegisterData, RegisterVars } from '../gql-types';

import AuthService from '../../shared/services/AuthService';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CheckIcon from '@mui/icons-material/Check';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Unstable_Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import MembershipService from '../services/MembershipService';
import PasswordField from '../../shared/components/PasswordField';
import ProfileService from '../../shared/services/ProfileService';
import ProgressMessageBox from './ProgressMessageBox';
import { PublicGraphQL } from '../gql';
import ReCAPTCHA from 'react-google-recaptcha';
import RegistrationResponse from '../models/RegistrationResponse';
import { SkinTheme } from '../../design/models/SkinTheme';
import { SkinThemeConsumer } from '../../shared/SkinThemeContext';
import SkinThemeService from '../../shared/services/SkinThemeService';
import TenantService from '../../shared/services/TenantService';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import UserProfileEditor from '../../shared/components/UserProfileEditor';
import WarningIcon from '@mui/icons-material/Warning';
import useDomainFlags from '../hooks/useDomainFlags';
import { useMutation } from '@apollo/client';
import useUserProfile from '../../shared/hooks/useUserProfile';

interface RegistrationProps {
  invitationToken: string;
  email?: string;
  onRegisterCompleted: (response: RegistrationResponse, email?: string) => void;
}

const Registration = (props: RegistrationProps) => {
  const [username, setUsername] = useState(props.email || '');
  const [email, setEmail] = useState(props.email || '');
  const [firstname, setFirstName] = useState('');
  const [lastname, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [checkedUsername, setCheckedUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean>();
  const [checkingEmailVerified, setCheckingEmailVerified] = useState<boolean>();
  const [emailIsAvailable, setEmailIsAvailable] = useState<
    /* available: */ true | /* verified: */ false | /* not checked: */ undefined
  >();
  const [recaptchaToken, setRecaptchaToken] = useState<boolean>();
  const invitationToken = props.invitationToken;
  const isMobile = useMediaQuery('(max-width: 600px)');
  const locationOrigin = window.location.origin;

  const { flags, flagsLoading } = useDomainFlags(TenantService.Tenant);

  /** Value indicating that we can register with non-email username. */
  const registerWithUsername =
    flags && !flags?.requireEmailVerification && !props.invitationToken;

  /** GET Tenant from Authservice */
  const tenant = AuthService.Tenant;

  // With email verification, the username *is* the email address.
  useEffect(() => {
    if (!registerWithUsername || EmailRegexp.test(username)) {
      setEmail(username);
    }
  }, [registerWithUsername, username]);

  const {
    handleProfileChange,
    loading,
    loadError,
    profileFields,
    userProfileFields,
    profileFieldInputs,
    allRequiredFilledIn
  } = useUserProfile(null, { forRegistration: true });

  const allRequiredProfileEntered = () => allRequiredFilledIn;

  /** Returns a value indicating whether the {@link username} value can be checked. */
  const canCheckUsername = () =>
    username && (registerWithUsername || EmailRegexp.test(username));

  const canSubmit = () => {
    // When checking username, "CONTINUE".
    if (!usernameAvailable) {
      return canCheckUsername();
    }

    // When validating inputs, "REGISTER".
    return (
      username.trim() &&
      EmailRegexp.test(email) &&
      firstname.trim() &&
      lastname.trim() &&
      password &&
      password.length >= MIN_PASSWORD_LENGTH &&
      allRequiredProfileEntered() &&
      (flags?.requireEmailVerification ||
        (!flags?.requireEmailVerification && recaptchaToken))
    );
  };

  // Error from registration portal.
  const [registrationErrors, setRegistrationErrors] = useState<string[]>();

  const [showWarning, setShowWarning] = useState<boolean>(false);

  const hasNoMinimumLength =
    password?.length < MIN_PASSWORD_LENGTH && showWarning;

  // Register a new or linked account.
  const [registerAsync, { loading: registrationInProgress }] = useMutation<
    RegisterData,
    RegisterVars
  >(PublicGraphQL.Mutation.REGISTER, {
    variables: {
      locationOrigin,
      tenant,
      input: {
        username,
        firstname,
        lastname,
        email,
        password,
        invitationToken
      },
      profileFieldInputs:
        ProfileService.convertToProfileFieldInputs(profileFieldInputs)
    },
    onCompleted: (data) => {
      if (data) {
        const userkey = data.register;
        props.onRegisterCompleted(userkey, email);
      }
    },
    onError: (error) => {
      setRegistrationErrors(error.graphQLErrors.map((ge) => ge.message));
    }
  });

  // Automatically check the email when using an invitation token.
  useEffect(() => {
    if (props.invitationToken) {
      checkUsernameAsync(username);
    }
  }, [props.invitationToken]);

  const handleUsernameClick = (e: MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    setUsernameAvailable(undefined);
    setCheckedUsername(undefined);
  };

  const checkUsernameAsync = async (username: string) => {
    if (!username || username === checkedUsername || !canCheckUsername()) {
      return;
    }
    setCheckingUsername(true);
    setUsernameAvailable(undefined);
    const available = await MembershipService.isUsernameAvailable(
      tenant,
      username
    );
    setCheckedUsername(username);
    setUsernameAvailable(available);
    if (email === username) {
      setEmailIsAvailable(true);
    }
    setCheckingUsername(false);
  };

  const checkEmailVerified = async (email: string) => {
    if (!email || !EmailRegexp.test(email)) {
      return;
    }
    setCheckingEmailVerified(true);
    setEmailIsAvailable(undefined);
    const verified = await MembershipService.isEmailVerified(tenant, email);
    setEmailIsAvailable(!verified);
    setCheckingEmailVerified(false);
  };

  const handleReCaptchaClick = async (token: string) => {
    if (token) {
      const verifiedToken = await MembershipService.getVerifyRecaptchaToken(
        token
      );
      setRecaptchaToken(verifiedToken);
    } else {
      setRecaptchaToken(false);
    }
  };

  const handleUsernameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    setUsernameAvailable(undefined);
    setCheckedUsername(undefined);
  };

  const handleUsernameBlur = async () => {
    await checkUsernameAsync(username);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (usernameAvailable === true) {
      await registerAsync();
    } else {
      await checkUsernameAsync(username);
    }
  };

  if (flagsLoading) {
    return <ProgressMessageBox message="Loading Registration..." />;
  }

  if (registrationInProgress) {
    return <ProgressMessageBox message="Registration in progress..." />;
  }

  return (
    <SkinThemeConsumer>
      {(themeOptions: SkinTheme) => (
        <>
          <form onSubmit={handleSubmit}>
            <Grid
              container
              justifyContent="center"
              className="login-body"
              direction="column"
            >
              <Grid>
                <Grid style={{ flex: 1 }}>
                  <div className="company-logo">
                    <img
                      src={SkinThemeService.getAssetUrl('auth', 'companyLogo')}
                      alt={
                        SkinThemeService.getThemeOptionValue(
                          themeOptions,
                          'auth',
                          'companyName'
                        ) as string
                      }
                    />
                  </div>
                  <CardHeader
                    title={
                      (SkinThemeService.getThemeOptionValue(
                        themeOptions,
                        'auth',
                        'companyName'
                      ) as string) || 'Register'
                    }
                    titleTypographyProps={{
                      variant: 'h2',
                      align: 'center'
                    }}
                    subheader={
                      SkinThemeService.getThemeOptionValue(
                        themeOptions,
                        'auth',
                        'companyDescription'
                      ) as string
                    }
                    subheaderTypographyProps={{
                      variant: 'h5',
                      align: 'center'
                    }}
                  ></CardHeader>
                  <CardContent className="registration-content">
                    <TextField
                      name="Username"
                      label={
                        registerWithUsername ? 'Email or Username' : 'Email'
                      }
                      type={registerWithUsername ? 'text' : 'email'}
                      variant="outlined"
                      fullWidth={true}
                      margin="dense"
                      size="small"
                      value={username}
                      disabled={
                        checkingUsername ||
                        !flags ||
                        (!!invitationToken && !!username)
                      }
                      error={usernameAvailable === false}
                      helperText={
                        usernameAvailable === false
                          ? `This account already exists.`
                          : ''
                      }
                      InputProps={{
                        endAdornment:
                          usernameAvailable === true ? (
                            <InputAdornment position="end">
                              <CheckIcon sx={{ color: 'green' }} />
                            </InputAdornment>
                          ) : null
                      }}
                      onChange={handleUsernameChange}
                      onBlur={handleUsernameBlur}
                      onClick={handleUsernameClick}
                      autoFocus
                    />
                    {usernameAvailable && (
                      <>
                        <TextField
                          name="Email"
                          label="Email address"
                          variant="outlined"
                          fullWidth={true}
                          margin="dense"
                          size="small"
                          value={email}
                          disabled={
                            (!!invitationToken && !!email) ||
                            EmailRegexp.test(username) ||
                            checkingEmailVerified
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {checkingEmailVerified ? (
                                  <CircularProgress size={12} />
                                ) : emailIsAvailable === true ? (
                                  <CheckIcon sx={{ color: 'green' }} />
                                ) : emailIsAvailable === false ? (
                                  <Tooltip title="Email is in use. Please use a different email.">
                                    <WarningIcon color="error" />
                                  </Tooltip>
                                ) : (
                                  <></>
                                )}
                              </InputAdornment>
                            )
                          }}
                          required
                          onChange={(e) => {
                            setEmail(e.target.value),
                              setEmailIsAvailable(undefined);
                          }}
                          onBlur={() => checkEmailVerified(email)}
                          style={
                            registerWithUsername
                              ? undefined
                              : { display: 'none' }
                          }
                        />
                        <TextField
                          name="FirstName"
                          label="First Name"
                          variant="outlined"
                          fullWidth={true}
                          margin="dense"
                          size="small"
                          value={firstname}
                          required
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                        <TextField
                          name="LastName"
                          label="Last Name"
                          variant="outlined"
                          fullWidth={true}
                          margin="dense"
                          size="small"
                          value={lastname}
                          required
                          onChange={(e) => setLastName(e.target.value)}
                        />
                        <PasswordField
                          label="Password"
                          value={password}
                          required
                          onChange={setPassword}
                          error={
                            hasNoMinimumLength
                              ? `Password must be at least ${MIN_PASSWORD_LENGTH} characters long`
                              : ''
                          }
                          onBlur={() => setShowWarning(true)}
                        />
                        <UserProfileEditor
                          hideSubFields
                          indicateErrorOnLoad={false}
                          loading={loading}
                          error={loadError}
                          profileFields={profileFields}
                          userProfileFields={userProfileFields}
                          values={profileFieldInputs}
                          onChange={handleProfileChange}
                        />
                        {!flags?.requireEmailVerification && (
                          <ReCAPTCHA
                            key={
                              isMobile
                                ? 'compact-recaptcha'
                                : 'normal-recaptcha'
                            }
                            sitekey={process.env.GOOGLE_RECAPTCHA_SITE_KEY}
                            onChange={handleReCaptchaClick}
                            size={isMobile ? 'compact' : 'normal'}
                            style={{ marginTop: 8 }}
                          />
                        )}
                      </>
                    )}
                    {/*   End of section that is shown when usernameAvailable is true */}
                  </CardContent>
                </Grid>
                <Grid>
                  <CardActions className="registration-actions">
                    <Grid
                      container
                      direction="column"
                      alignItems="center"
                      spacing={2}
                      sx={{ width: '100%' }}
                    >
                      <Grid sx={{ width: '100%' }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          color="primary"
                          name="button"
                          value="login"
                          sx={{ width: '100%' }}
                          disabled={
                            !canSubmit() ||
                            checkingUsername ||
                            checkingEmailVerified ||
                            (usernameAvailable && emailIsAvailable !== true)
                          }
                        >
                          {usernameAvailable
                            ? 'Register'
                            : checkingUsername
                            ? 'Checking...'
                            : 'Continue'}
                        </Button>
                      </Grid>
                      {!invitationToken && (
                        <Grid container direction="column" alignItems="center">
                          <Grid>
                            <Typography variant="body1">
                              Already have an account?
                            </Typography>
                          </Grid>
                          <Grid>
                            <Button
                              color="secondary"
                              onClick={() => (window.location.href = '/')}
                            >
                              Login here
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </CardActions>
                </Grid>
              </Grid>
            </Grid>
          </form>
          {registrationErrors && (
            <Dialog open={!!registrationErrors}>
              <DialogTitle>Registration error</DialogTitle>
              <DialogContent>
                <Typography>
                  Your account could not be created. Please check the following
                  and try again.
                </Typography>
                <ul>
                  {registrationErrors.map((m) => (
                    <li>{m}</li>
                  ))}
                </ul>
              </DialogContent>
              <DialogActions>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setRegistrationErrors(undefined)}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </>
      )}
    </SkinThemeConsumer>
  );
};

export default Registration;
